import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataForm, SourceDataFormStatus, SourceDataPriority } from "@/api/dtos";
import Field from "@/components/forms/fields/Field";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Select from "@/components/forms/inputs/Select";
import Text from "@/components/forms/inputs/Text";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationType } from "@/hooks/form/configuration";
import useFormConfiguration, { copyConfigurationSourceDataFormSettings } from "@/hooks/form/configuration/useFormConfiguration";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Divider, List, Paper, Typography, styled } from "@mui/material";
import { useFormActions } from "@ngt/forms-core";
import { immerable } from "immer/dist/immer";
import { FC, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import AlertMarkdown from "../../inputs/AlertMarkdown";
import Multiselect from "../../inputs/Multiselect";
import UneditableText from "../../inputs/UneditableText";
import { IConfigurationSourceDataBulkEditField, IConfigurationSourceDataBulkEditSettings } from "./ConfigurationSourceDataBulkEdit";
import { DetailsListItem, DetailsListItemButton, DetailsListItemIcon, DetailsListItemText } from "./ConfigurationSourceDataFormDetails";
import ConfigurationSourceDataFormLinkAlert from "./ConfigurationSourceDataFormLinkAlert";


const FormContainer = styled(Paper)(({ theme }) => ({ 
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
}));

const ListContainer = styled(Paper)(({ theme }) => ({ 
    marginBottom: theme.spacing(2)
}));

const PriorityBox = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    gap: theme.spacing(2),
    alignItems: 'baseline',
    height: theme.spacing(3),

    '& > *:first-of-type': {
        alignSelf: 'center'
    }
}));

const enabledOptions = [
    {
        id: 1,
        name: 'Disabled',
        description: 'The form is disabled and will not be displayed.'
    },
    {
        id: 2,
        name: 'Enabled',
        description: 'The form is enabled and will be displayed.'
    }
]

const noteOptions = [
    {
        id: 1,
        name: 'Not Allowed',
        description: 'Form notes cannot be viewed, added or updated.'
    },
    {
        id: 2,
        name: 'Allowed',
        description: 'Form notes can be viewed, added and updated.'
    }
]

const reviewOptions = [
    {
        id: 1,
        name: 'Not Allowed',
        description: 'Items cannot be flagged as reviewed.'
    },
    {
        id: 2,
        name: 'Allowed',
        description: 'Items can be flagged as reviewed.'
    }
]

const verificationOptions = [
    {
        id: 1,
        name: 'Not Allowed',
        description: 'Items cannot be flagged as verified.'
    },
    {
        id: 2,
        name: 'Allowed',
        description: 'Items can be flagged as verified.'
    }
]

const notApplicableOptions = [
    {
        id: 1,
        name: 'Not Allowed',
        description: 'Items cannot be flagged as not applicable.'
    },
    {
        id: 2,
        name: 'Allowed',
        description: 'Items can be flagged as not applicable.'
    }
]


const priorityOptions = [
    {
        id: SourceDataPriority.Inherit,
        name: <PriorityBox><FontAwesomeIcon icon={AppSettings.icons.priorityInherit} fixedWidth /> Inherit</PriorityBox>,
        description: 'The importance of the form is inherited from the associated event.'
    },
    {
        id: SourceDataPriority.Low,
        name: <PriorityBox sx={{ color: 'success.main'}}><FontAwesomeIcon icon={AppSettings.icons.priorityLow} fixedWidth /> Low</PriorityBox>,
        description: 'The form is of low importance.'
    },
    {
        id: SourceDataPriority.Normal,
        name: <PriorityBox><FontAwesomeIcon icon={AppSettings.icons.priorityNormal} fixedWidth /> Normal</PriorityBox>,
        description: 'The form is of normal importance.'
    },
    {
        id: SourceDataPriority.High,
        name: <PriorityBox sx={{ color: 'warning.main'}}><FontAwesomeIcon icon={AppSettings.icons.priorityHigh} fixedWidth /> High</PriorityBox>,
        description: 'The form is of high importance.'
    },
    {
        id: SourceDataPriority.Critical,
        name: <PriorityBox sx={{ color: 'error.main'}}><FontAwesomeIcon icon={AppSettings.icons.priorityCritical} fixedWidth /> Critical</PriorityBox>,
        description: 'The form is of critical importance.'
    }
]

const statusOptions = [
    {
        id: SourceDataFormStatus.None,
        name: <PriorityBox sx={{ color: 'error.main'}}><FontAwesomeIcon icon={AppSettings.icons.statusNone} fixedWidth /> None</PriorityBox>,
        description: 'The form has not started data entry.'
    },
    {
        id: SourceDataFormStatus.Started,
        name: <PriorityBox sx={{ color: 'warning.main'}}><FontAwesomeIcon icon={AppSettings.icons.statusStarted} fixedWidth /> Started</PriorityBox>,
        description: 'The form has started data entry.'
    },
    {
        id: SourceDataFormStatus.Complete,
        name: <PriorityBox sx={{ color: 'success.main'}}><FontAwesomeIcon icon={AppSettings.icons.statusComplete} fixedWidth /> Complete</PriorityBox>,
        description: 'The form has completed data entry.'
    }
]

const enableParse = (value?: number) => value === 1 ? false : value === 2 ? true : undefined;
const enableConvert = (value?: boolean) => value === false ? 1 : value === true ? 2 : undefined;

const Fields: Record<string, IConfigurationSourceDataBulkEditField<NonNullable<ReturnType<typeof useFormConfiguration>>>> = {
    OriginalName: {
        propertyPath: 'originalName',
        name: 'Default Name',
        description: 'The Default Name of the form.',
        Component: ({ propertyPath, name, description, margin, data}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    margin={margin}
                    component={UneditableText}
                    disabled
                    value={data?.formMetadata?.name}
                />
            )     
        }
    },
    Name: {
        propertyPath: 'name',
        name: 'Name',
        description: 'Enter a name to override the Default Name of the form.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    margin={margin}
                    component={Text}
                    disabled={disabled}
                />
            )     
        }
    },
    Enabled: {
        propertyPath: 'enabled',
        name: 'Status',
        description: 'The status of the form.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    FormControlProps={{
                        margin: margin
                    }}
                    parse={enableParse as any}
                    convert={enableConvert as any}
                    nullOption={false}
                    component={Select}
                    options={enabledOptions}
                    disabled={disabled}
                />
            )     
        }
    },
    Priority: {
        propertyPath: 'priority',
        name: 'Priority',
        description: 'The priority of the form.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {     
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    FormControlProps={{
                        margin: margin
                    }}
                    nullOption={false}
                    component={Select}
                    options={priorityOptions}
                    disabled={disabled}
                />
            )     
        }
    },
    Statuses: {
        propertyPath: 'statuses',
        name: 'Statuses',
        description: 'The statuses that the form must be one of to be pulled from OpenClinica.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {  
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    FormControlProps={{
                        margin: margin
                    }}
                    component={Multiselect}
                    options={statusOptions}
                    disabled={disabled}
                />
            )     
        }
    },
    AllowReview: {
        propertyPath: 'allowReview',
        name: 'Allow Review',
        description: 'Whether the items contained within can be flagged as reviewed.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    FormControlProps={{
                        margin: margin
                    }}
                    parse={enableParse as any}
                    convert={enableConvert as any}
                    nullOption={false}
                    component={Select}
                    options={reviewOptions}
                    disabled={disabled}
                />
            )     
        }
    },
    AllowVerification: {
        propertyPath: 'allowVerification',
        name: 'Allow Verification',
        description: 'Whether the items contained within can be flagged as verified.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {      
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    FormControlProps={{
                        margin: margin
                    }}
                    parse={enableParse as any}
                    convert={enableConvert as any}
                    nullOption={false}
                    component={Select}
                    options={verificationOptions}
                    disabled={disabled}
                />
            )     
        }
    },
    AllowNotApplicable: {
        propertyPath: 'allowNotApplicable',
        name: 'Allow Not Applicable',
        description: 'Whether the items contained within can be flagged as not applicable.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {      
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    FormControlProps={{
                        margin: margin
                    }}
                    parse={enableParse as any}
                    convert={enableConvert as any}
                    nullOption={false}
                    component={Select}
                    options={notApplicableOptions}
                    disabled={disabled}
                />
            )     
        }
    },
    AllowNotes: {
        propertyPath: 'allowNotes',
        name: 'Allow Notes',
        description: 'Whether viewing, adding and updating of discrepancy notes attached directly to the form is enabled.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {   
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    FormControlProps={{
                        margin: margin
                    }}
                    parse={enableParse as any}
                    convert={enableConvert as any}
                    nullOption={false}
                    component={Select}
                    options={noteOptions}
                    disabled={disabled}
                />
            )     
        }
    },
    AlertSuccess: {
        propertyPath: 'alertSuccess',
        name: 'Message - Success Styling',
        description: 'Enter a message that will be displayed in success styling (green) at the top of every page related to this form.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    margin={margin}
                    component={AlertMarkdown}
                    severity="success"
                    disabled={disabled}
                />
            )     
        }
    },
    AlertInformation: {
        propertyPath: 'alertInformation',
        name: 'Message - Information Styling',
        description: 'Enter a message that will be displayed in information styling (blue) at the top of every page related to this form.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    margin={margin}
                    component={AlertMarkdown}
                    severity="info"
                    disabled={disabled}
                />
            )     
        }
    },
    AlertWarning: {
        propertyPath: 'alertWarning',
        name: 'Message - Warning Styling',
        description: 'Enter a message that will be displayed in warning styling (yellow) at the top of every page related to this form.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    margin={margin}
                    component={AlertMarkdown}
                    severity="warning"
                    disabled={disabled}
                />
            )     
        }
    },
    AlertError: {
        propertyPath: 'alertError',
        name: 'Message - Error Styling',
        description: 'Enter a message that will be displayed in error styling (red) at the top of every page related to this form.',
        Component: ({ disabled, propertyPath, name, description, margin}) => {
            return (
                <InputOnlyField
                    propertyPath={propertyPath}
                    label={name}
                    helperText={description}
                    margin={margin}
                    component={AlertMarkdown}
                    severity="error"
                    disabled={disabled}
                />
            )     
        }
    }
}

export const ConfigurationSourceDataFormBulkEditSettings: IConfigurationSourceDataBulkEditSettings<ConfigurationSourceDataForm, NonNullable<ReturnType<typeof useFormConfiguration>>> = {
    getLocalConfigurationPropertyPath: (item) =>  item.studyEventOid ?
        `configuration.forms.${item.studyEventOid}.${item.caseReportFormOid}` :
        `configuration.forms.${AppSettings.metadata.studyEventNullKey}.${item.caseReportFormOid}`,
    getParentConfigurationPropertyPath: (item) => {
        if (item.studyEventOid && !item.trialEventFormConfiguration?.linked) {
            return `trialConfiguration.forms.${item.studyEventOid}.${item.caseReportFormOid}`;
        }
        else if (item.studyEventOid && !item.formConfiguration?.linked) {
            return `configuration.forms.${AppSettings.metadata.studyEventNullKey}.${item.caseReportFormOid}`
        }
        else {
            return `trialConfiguration.forms.${AppSettings.metadata.studyEventNullKey}.${item.caseReportFormOid}`
        }
    },
    getKey: (item) => item.caseReportFormOid!,
    copyConfiguration: copyConfigurationSourceDataFormSettings,
    createNewConfiguration: () => new ConfigurationSourceDataForm({}),
    mapping: {
        id: undefined,
        trialId: undefined,
        configurationSourceDataId: undefined,
        studyEventOid: undefined,
        caseReportFormOid: undefined,
        name: {
            name: Fields.Name.name,
            description: Fields.Name.description,
            required: false,
            component: (data) => {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                        }}
                    >
                        <Fields.OriginalName.Component
                            propertyPath={Fields.OriginalName.propertyPath}
                            name={Fields.OriginalName.name}
                            description={undefined}
                            margin="none"
                            data={data}
                            disabled={data.linked}
                        />
                        <Fields.Name.Component
                            propertyPath={Fields.Name.propertyPath}
                            name={Fields.Name.name}
                            description={undefined}
                            margin="none"
                            data={data}
                            disabled={data.linked}
                        />
                    </Box>  
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.Name.Component
                        propertyPath="value"
                        name={Fields.Name.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        enabled: {
            name: Fields.Enabled.name,
            description: Fields.Enabled.description,
            required: true,
            component: (data) => {
                return (      
                    <Fields.Enabled.Component
                        propertyPath={Fields.Enabled.propertyPath}
                        name={Fields.Enabled.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.Enabled.Component
                        propertyPath="value"
                        name={Fields.Enabled.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        priority: {
            name: Fields.Priority.name,
            description: Fields.Priority.description,
            required: true,
            component: (data) => {
                return (      
                    <Fields.Priority.Component
                        propertyPath={Fields.Priority.propertyPath}
                        name={Fields.Priority.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.Priority.Component
                        propertyPath="value"
                        name={Fields.Priority.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        statuses: {
            name: Fields.Statuses.name,
            description: Fields.Statuses.description,
            required: false,
            component: (data) => {
                return (      
                    <Fields.Statuses.Component
                        propertyPath={Fields.Statuses.propertyPath}
                        name={Fields.Statuses.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.Statuses.Component
                        propertyPath="value"
                        name={Fields.Statuses.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        allowReview: {
            name: Fields.AllowReview.name,
            description: Fields.AllowReview.description,
            required: true,
            component: (data) => {
                return (      
                    <Fields.AllowReview.Component
                        propertyPath={Fields.AllowReview.propertyPath}
                        name={Fields.AllowReview.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AllowReview.Component
                        propertyPath="value"
                        name={Fields.AllowReview.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        allowVerification: {
            name: Fields.AllowVerification.name,
            description: Fields.AllowVerification.description,
            required: true,
            component: (data) => {
                return (      
                    <Fields.AllowVerification.Component
                        propertyPath={Fields.AllowVerification.propertyPath}
                        name={Fields.AllowVerification.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AllowVerification.Component
                        propertyPath="value"
                        name={Fields.AllowVerification.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        allowNotApplicable: {
            name: Fields.AllowNotApplicable.name,
            description: Fields.AllowNotApplicable.description,
            required: true,
            component: (data) => {
                return (      
                    <Fields.AllowNotApplicable.Component
                        propertyPath={Fields.AllowNotApplicable.propertyPath}
                        name={Fields.AllowNotApplicable.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AllowNotApplicable.Component
                        propertyPath="value"
                        name={Fields.AllowNotApplicable.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        allowNotes: {
            name: Fields.AllowNotes.name,
            description: Fields.AllowNotes.description,
            required: true,
            component: (data) => {
                return (      
                    <Fields.AllowNotes.Component
                        propertyPath={Fields.AllowNotes.propertyPath}
                        name={Fields.AllowNotes.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AllowNotes.Component
                        propertyPath="value"
                        name={Fields.AllowNotes.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },

        alertSuccess: {
            name: Fields.AlertSuccess.name,
            description: Fields.AlertSuccess.description,
            required: false,
            component: (data) => {
                return (      
                    <Fields.AlertSuccess.Component
                        propertyPath={Fields.AlertSuccess.propertyPath}
                        name={Fields.AlertSuccess.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AlertSuccess.Component
                        propertyPath="value"
                        name={Fields.AlertSuccess.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        alertInformation: {
            name: Fields.AlertInformation.name,
            description: Fields.AlertInformation.description,
            required: false,
            component: (data) => {
                return (      
                    <Fields.AlertInformation.Component
                        propertyPath={Fields.AlertInformation.propertyPath}
                        name={Fields.AlertInformation.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AlertInformation.Component
                        propertyPath="value"
                        name={Fields.AlertInformation.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        alertWarning: {
            name: Fields.AlertWarning.name,
            description: Fields.AlertWarning.description,
            required: false,
            component: (data) => {
                return (      
                    <Fields.AlertWarning.Component
                        propertyPath={Fields.AlertWarning.propertyPath}
                        name={Fields.AlertWarning.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AlertWarning.Component
                        propertyPath="value"
                        name={Fields.AlertWarning.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },
        alertError: {
            name: Fields.AlertError.name,
            description: Fields.AlertError.description,
            required: false,
            component: (data) => {
                return (      
                    <Fields.AlertError.Component
                        propertyPath={Fields.AlertError.propertyPath}
                        name={Fields.AlertError.name}
                        description={undefined}
                        margin="none"
                        data={data}
                        disabled={data.linked}
                    />
                )
            },
            bulkEditComponent: () => {
                return (
                    <Fields.AlertError.Component
                        propertyPath="value"
                        name={Fields.AlertError.name}
                        description={undefined}
                        margin="none"
                        data={undefined}
                        disabled={false}
                    />
                )
            }
        },

        linked: undefined,
        enteredBy: undefined,
        enteredDate: undefined,
        modifiedBy: undefined,
        modifiedDate: undefined,

        [immerable]: undefined  
    }
}

interface IConfigurationSourceDataFormFormProps {
    data: NonNullable<ReturnType<typeof useFormConfiguration>>;
    onLinkUpdate: () => void;
}

export const ValidateConfigurationSourceDataEventForm = () => {
    
}

const ConfigurationSourceDataFormForm: FC<IConfigurationSourceDataFormFormProps> = ({
    data,
    onLinkUpdate
}) => {
    const localConfigurationPropertyPath = useMemo(() => {
        return ConfigurationSourceDataFormBulkEditSettings.getLocalConfigurationPropertyPath(data);
    }, [data]);

    const parentConfigurationPropertyPath = useMemo(() => {
        return ConfigurationSourceDataFormBulkEditSettings.getParentConfigurationPropertyPath(data);
    }, [data]);

    const { data: trial } = useContext(TrialContext);
    
    const { setFieldValue, getFieldValue } = useFormActions();

    const disabled = data.linked ?? false;

    const unlink = () => {
        setFieldValue<ConfigurationSourceDataForm>(localConfigurationPropertyPath, currentValue => {
            currentValue.linked = false;

            const linkedValue = getFieldValue<ConfigurationSourceDataForm>(parentConfigurationPropertyPath);

            if (linkedValue) {
                return ConfigurationSourceDataFormBulkEditSettings.copyConfiguration(linkedValue, currentValue);
            }

            return currentValue;
        }, true, true, true);
        
        onLinkUpdate();
    };

    const link = () => {
        setFieldValue<ConfigurationSourceDataForm>(localConfigurationPropertyPath, currentValue => {
            currentValue.linked = true;

            return ConfigurationSourceDataFormBulkEditSettings.copyConfiguration(
                ConfigurationSourceDataFormBulkEditSettings.createNewConfiguration(data), 
                currentValue
            )
        }, true, true, true);

        onLinkUpdate();
    };

    const linkType = useMemo(() => {
        if (data.studyEventOid && !data.trialEventFormConfiguration?.linked) {
            return ConfigurationType.TrialEvent
        }
        else if (data.studyEventOid && !data.formConfiguration?.linked) {
            return ConfigurationType.Form
        }
        else {
            return ConfigurationType.Trial
        }
    }, [data])

    return (
        <Field propertyPath={data.linked ? parentConfigurationPropertyPath : localConfigurationPropertyPath}>
            <ConfigurationSourceDataFormLinkAlert 
                entityName="form"
                type={linkType}
                linked={data.linked ?? true}
                trial={trial}
                onLink={link}
                onUnlink={unlink}
            />
            <Typography variant="h2" gutterBottom>
                Details
            </Typography>
            <FormContainer>
                <Fields.OriginalName.Component 
                    data={data}
                    propertyPath={Fields.OriginalName.propertyPath}
                    name={Fields.OriginalName.name}
                    description={Fields.OriginalName.description}
                    disabled={disabled}
                    margin="normal"
                />
                <Fields.Name.Component 
                    data={data}
                    propertyPath={Fields.Name.propertyPath}
                    name={Fields.Name.name}
                    description={Fields.Name.description}
                    disabled={disabled}
                    margin="normal"
                />         
                <Fields.Enabled.Component 
                    data={data}
                    propertyPath={Fields.Enabled.propertyPath}
                    name={Fields.Enabled.name}
                    description={Fields.Enabled.description}
                    disabled={disabled}
                    margin="normal"
                />     
                <Fields.Priority.Component 
                    data={data}
                    propertyPath={Fields.Priority.propertyPath}
                    name={Fields.Priority.name}
                    description={Fields.Priority.description}
                    disabled={disabled}
                    margin="normal"
                />
                <Fields.Statuses.Component 
                    data={data}
                    propertyPath={Fields.Statuses.propertyPath}
                    name={Fields.Statuses.name}
                    description={Fields.Statuses.description}
                    disabled={disabled}
                    margin="normal"
                /> 
                <Fields.AllowReview.Component 
                    data={data}
                    propertyPath={Fields.AllowReview.propertyPath}
                    name={Fields.AllowReview.name}
                    description={Fields.AllowReview.description}
                    disabled={disabled}
                    margin="normal"
                /> 
                <Fields.AllowVerification.Component 
                    data={data}
                    propertyPath={Fields.AllowVerification.propertyPath}
                    name={Fields.AllowVerification.name}
                    description={Fields.AllowVerification.description}
                    disabled={disabled}
                    margin="normal"
                /> 
                <Fields.AllowNotApplicable.Component 
                    data={data}
                    propertyPath={Fields.AllowNotApplicable.propertyPath}
                    name={Fields.AllowNotApplicable.name}
                    description={Fields.AllowNotApplicable.description}
                    disabled={disabled}
                    margin="normal"
                /> 
                <Fields.AllowNotes.Component 
                    data={data}
                    propertyPath={Fields.AllowNotes.propertyPath}
                    name={Fields.AllowNotes.name}
                    description={Fields.AllowNotes.description}
                    disabled={disabled}
                    margin="normal"
                />     
            </FormContainer>
            <Typography variant="h2" gutterBottom>
                Messages
            </Typography>
            <FormContainer>
                <Fields.AlertSuccess.Component 
                    data={data}
                    propertyPath={Fields.AlertSuccess.propertyPath}
                    name={Fields.AlertSuccess.name}
                    description={Fields.AlertSuccess.description}
                    disabled={disabled}
                    margin="normal"
                />
                <Fields.AlertInformation.Component 
                    data={data}
                    propertyPath={Fields.AlertInformation.propertyPath}
                    name={Fields.AlertInformation.name}
                    description={Fields.AlertInformation.description}
                    disabled={disabled}
                    margin="normal"
                />
                <Fields.AlertWarning.Component 
                    data={data}
                    propertyPath={Fields.AlertWarning.propertyPath}
                    name={Fields.AlertWarning.name}
                    description={Fields.AlertWarning.description}
                    disabled={disabled}
                    margin="normal"
                />  
                <Fields.AlertError.Component 
                    data={data}
                    propertyPath={Fields.AlertError.propertyPath}
                    name={Fields.AlertError.name}
                    description={Fields.AlertError.description}
                    disabled={disabled}
                    margin="normal"
                />
            </FormContainer>

            <Typography variant="h2" gutterBottom>
                Related
            </Typography>
            <ListContainer>
                <List disablePadding>
                    <DetailsListItem 
                        disablePadding
                    >
                        <DetailsListItemButton 
                            //@ts-ignore-next-line
                            component={Link} 
                            to={data.configurationFormVersionsLink}
                        >
                            <DetailsListItemIcon>
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.formVersion} />
                            </DetailsListItemIcon>
                            <DetailsListItemText 
                                primary="Form Versions" 
                            />
                        </DetailsListItemButton>
                    </DetailsListItem>
                    <Divider />
                </List>
            </ListContainer>
        </Field>
    );
};

export default ConfigurationSourceDataFormForm;